import {
  ListBookedConsultationsRequest,
  BookConsultationRequest,
  UnbookConsultationRequest,
  DownloadConsultationCalendarRequest,
} from '@taxfyle/api-internal/internal/consultation_pb'
import { ClaimConsultationJobRequest } from '@taxfyle/api-internal/internal/job_pb'
import { Interval } from '@taxfyle/api-internal/shared/types/interval_pb'
import { TimeRange } from 'jobs/TimeRange'
import { PickupResult } from 'opportunities/OpportunityStore'
import { ISOToTimestamp } from 'utils/grpc-util'
import { WorkAPI } from '../API'
import Consultation from './Consultation'
import { FetchJob } from 'jobs/JobStore'
import { PrimeRoom } from 'messaging/rooms/RoomStore'

export default class ConsultationStore {
  constructor(
    private api: WorkAPI,
    private refreshJob: FetchJob,
    private primeRoom: PrimeRoom,
  ) {}

  /**
   * Fetch booked consultations for the current user
   *
   * @returns
   */
  async fetchBookedConsultations() {
    const result = await this.api.consultations.listBookedConsultations(
      new ListBookedConsultationsRequest(),
    )
    const attrs = result.toObject()
    return attrs.appointmentsList.map((c) =>
      Consultation.fromJSON(
        {
          providerUserPublicId: c.proPublicId,
          clientUserPublicId: c.clientPublicId,
          ...c,
        },
        this.primeRoom,
      ),
    )
  }

  /**
   * Claims a consultation job
   *
   * A booked time is not required for tax jobs but is required for consultation jobs.
   * NOTICE: Making illegal states representable like this is considered a bad practice.
   */
  async pickupConsultationJob(id: string, bookedTime?: TimeRange) {
    const request = new ClaimConsultationJobRequest().setId(id.toString())

    if (bookedTime) {
      request.setBookedTime(
        new Interval()
          .setStartTime(ISOToTimestamp(bookedTime.startTime.toISOString()))
          .setEndTime(ISOToTimestamp(bookedTime.endTime.toISOString())),
      )
    }

    await this.api.jobsV3.claimConsultationJob(request)

    await this.refreshJob(id)

    return PickupResult.Success
  }

  /**
   * Books a consultation
   *
   * @param jobId
   * @param bookedTime
   * @returns
   */
  async bookConsultation(jobId: string, bookedTime: TimeRange) {
    await this.api.consultations.bookConsultation(
      new BookConsultationRequest()
        .setId(jobId.toString())
        .setBookedTime(
          new Interval()
            .setStartTime(ISOToTimestamp(bookedTime.startTime.toISOString()))
            .setEndTime(ISOToTimestamp(bookedTime.endTime.toISOString())),
        ),
    )

    await this.refreshJob(jobId)
  }

  /**
   * Withdraws a consultation job
   *
   * @param jobId
   * @returns
   */
  async unbookConsultation(jobId: string) {
    await this.api.consultations.unbookConsultation(
      new UnbookConsultationRequest().setId(jobId),
    )
    await this.refreshJob(jobId)
  }

  /**
   * Downloads a consultation calendar
   *
   * @param jobId
   * @returns
   */
  async downloadConsultationCalendar(jobId: string) {
    return await this.api.consultations
      .downloadConsultationCalendar(
        new DownloadConsultationCalendarRequest().setId(jobId),
      )
      .then(
        (res) =>
          new File([res.getData_asU8()], 'calendar.ics', {
            type: 'text/calendar',
          }),
      )
  }
}
